import { PrismFrameMetrics } from './prismFrameMetrics';
import { PrismRotationAnimator, PrismPointerTracker } from './prismRotation';
import type { PrismAnimationType, PrismFpsSampleRef, PrismRuntimeConfig, PrismScene } from './prismTypes';

interface CreatePrismRenderLoopInput {
  scene: PrismScene;
  animationType: PrismAnimationType;
  runtimeConfig: PrismRuntimeConfig;
  pointer: PrismPointerTracker;
  onFpsSampleRef: PrismFpsSampleRef;
  onError?: (error: unknown) => void;
  /** Coverage state at construction, so a remount never renders a frame under an overlay. */
  initiallyPaused: boolean;
}

/** Coverage-driven playback, owned separately from visibility's run request. */
export interface PrismPlaybackControls {
  pause: () => void;
  resume: () => void;
}

export interface PrismRenderLoop extends PrismPlaybackControls {
  startRAF: () => void;
  stopRAF: () => void;
  destroy: () => void;
  onContextLost: (event: Event) => void;
}

interface GetPrismEffectiveTimestampInput {
  timestamp: number;
  totalPausedMs: number;
}

/**
 * Shift raw rAF timestamps back by every millisecond spent paused, so shader time,
 * rotation and frame metrics all resume where they stopped instead of jumping.
 */
export function getPrismEffectiveTimestamp({
  timestamp,
  totalPausedMs,
}: GetPrismEffectiveTimestampInput): number {
  return timestamp - totalPausedMs;
}

export function createPrismRenderLoop({
  scene,
  animationType,
  runtimeConfig,
  pointer,
  onFpsSampleRef,
  onError,
  initiallyPaused,
}: CreatePrismRenderLoopInput): PrismRenderLoop {
  const animation = new PrismRotationAnimator({ animationType, runtimeConfig, pointer });
  const metrics = new PrismFrameMetrics();
  const startTime = performance.now();
  let raf = 0;
  let hasFailed = false;
  let runRequested = false;
  let pausedAt: number | null = initiallyPaused ? performance.now() : null;
  let totalPausedMs = 0;

  const cancelRAF = (): void => {
    if (!raf) return;
    cancelAnimationFrame(raf);
    raf = 0;
  };

  const stopRAF = (): void => {
    runRequested = false;
    cancelRAF();
  };

  const fail = (error: unknown): void => {
    if (hasFailed) return;
    hasFailed = true;
    stopRAF();
    onError?.(error);
  };

  const render = (timestamp: number): void => {
    const effectiveTimestamp = getPrismEffectiveTimestamp({ timestamp, totalPausedMs });
    const frame = preparePrismFrame({ scene, animation, timestamp: effectiveTimestamp, startTime });
    const fpsSampler = onFpsSampleRef.current;
    const renderResult = renderPrismSceneFrame({ scene, measuring: Boolean(fpsSampler), fail });
    if (!renderResult.rendered) return;

    if (fpsSampler) {
      metrics.record({
        timestamp: effectiveTimestamp,
        renderMs: renderResult.renderMs,
        fpsSampler,
      });
    }
    raf = requestNextPrismFrame({ continueRAF: frame.continueRAF, hasFailed, render });
  };

  const startRAF = (): void => {
    runRequested = true;
    if (raf || hasFailed || pausedAt != null) return;
    raf = requestAnimationFrame(render);
  };

  const pause = (): void => {
    if (pausedAt != null) return;
    pausedAt = performance.now();
    cancelRAF();
  };

  const resume = (): void => {
    if (pausedAt != null) {
      totalPausedMs += performance.now() - pausedAt;
      pausedAt = null;
    }
    if (runRequested) startRAF();
  };

  const destroy = (): void => {
    hasFailed = true;
    stopRAF();
  };

  const onContextLost = (event: Event): void => {
    event.preventDefault();
    fail(new Error('Prism WebGL context was lost'));
  };

  return {
    startRAF,
    stopRAF,
    destroy,
    onContextLost,
    pause,
    resume,
  };
}

interface PreparedPrismFrame {
  continueRAF: boolean;
}

interface PreparePrismFrameInput {
  scene: PrismScene;
  animation: PrismRotationAnimator;
  timestamp: number;
  startTime: number;
}

function preparePrismFrame({ scene, animation, timestamp, startTime }: PreparePrismFrameInput): PreparedPrismFrame {
  const time = (timestamp - startTime) * 0.001;
  scene.program.uniforms.iTime.value = time;
  const rotation = animation.update(time);
  scene.program.uniforms.uRot.value = rotation.matrix;

  return {
    continueRAF: rotation.continueRAF,
  };
}

interface RenderPrismSceneFrameInput {
  scene: PrismScene;
  measuring: boolean;
  fail: (error: unknown) => void;
}

interface RenderPrismSceneFrameResult {
  rendered: boolean;
  renderMs: number;
}

function renderPrismSceneFrame({ scene, measuring, fail }: RenderPrismSceneFrameInput): RenderPrismSceneFrameResult {
  const renderStart = getPrismRenderStart(measuring);

  try {
    scene.renderer.render({ scene: scene.mesh });
  } catch (error) {
    fail(error);
    return { rendered: false, renderMs: 0 };
  }

  return {
    rendered: true,
    renderMs: getPrismRenderMs({ measuring, renderStart }),
  };
}

function getPrismRenderStart(measuring: boolean): number {
  if (!measuring) return 0;
  return performance.now();
}

function getPrismRenderMs({ measuring, renderStart }: { measuring: boolean; renderStart: number }): number {
  if (!measuring) return 0;
  return performance.now() - renderStart;
}

interface RequestNextPrismFrameInput {
  continueRAF: boolean;
  hasFailed: boolean;
  render: (timestamp: number) => void;
}

function requestNextPrismFrame({ continueRAF, hasFailed, render }: RequestNextPrismFrameInput): number {
  if (!continueRAF) return 0;
  if (hasFailed) return 0;
  return requestAnimationFrame(render);
}
